/**
 * hitTest.ts — what is under the pointer.
 *
 * Hit testing is interaction, so it is the client's job (§21). It runs against
 * the projection the Engine produced and asks a purely geometric question; it
 * never decides whether two things MAY connect, which is an engineering rule
 * the Engine owns and answers when the intent is submitted.
 *
 * Priority is ports, then traces, then symbol bodies. Ports win because they
 * are small and are what a user is aiming at when they are near one; symbol
 * bodies lose because they are large and would otherwise swallow every port on
 * their own boundary.
 */

import type { DiagramRenderProjection, ProjectedPort, ProjectedSymbol, ProjectedTrace } from "../types/contract";

export type Hit =
  | { kind: "port"; symbol: ProjectedSymbol; port: ProjectedPort; distanceMm: number }
  | { kind: "trace"; trace: ProjectedTrace; distanceMm: number }
  | { kind: "symbol"; symbol: ProjectedSymbol }
  | { kind: "none" };

export interface HitOptions {
  /** Pick radius in WORLD mm — derive it from the zoom so it is constant on screen. */
  toleranceMm: number;
}

export function hitTest(projection: DiagramRenderProjection, world: { x: number; y: number }, options: HitOptions): Hit {
  const portHit = nearestPort(projection, world, options.toleranceMm);
  if (portHit) return portHit;

  const traceHit = nearestTrace(projection, world, options.toleranceMm);
  if (traceHit) return traceHit;

  // Reverse order: later symbols are drawn on top, so they are hit first.
  for (let i = projection.symbols.length - 1; i >= 0; i -= 1) {
    const symbol = projection.symbols[i];
    if (symbol && containsPoint(symbol.bbox, world)) return { kind: "symbol", symbol };
  }
  return { kind: "none" };
}

export function nearestPort(
  projection: DiagramRenderProjection,
  world: { x: number; y: number },
  toleranceMm: number,
): Extract<Hit, { kind: "port" }> | undefined {
  let best: Extract<Hit, { kind: "port" }> | undefined;
  for (const symbol of projection.symbols) {
    for (const port of symbol.ports) {
      // Both the indicator centre and the attachment point are legitimate
      // targets: a user aims at the dot, but a trace arrives at the boundary.
      const distanceMm = Math.min(distance(port.boundary, world), distance(port.center, world));
      if (distanceMm > toleranceMm) continue;
      if (!best || distanceMm < best.distanceMm) best = { kind: "port", symbol, port, distanceMm };
    }
  }
  return best;
}

export function nearestTrace(
  projection: DiagramRenderProjection,
  world: { x: number; y: number },
  toleranceMm: number,
): Extract<Hit, { kind: "trace" }> | undefined {
  let best: Extract<Hit, { kind: "trace" }> | undefined;
  for (const trace of projection.traces) {
    let distanceMm = Infinity;
    for (let i = 1; i < trace.path.length; i += 1) {
      const a = trace.path[i - 1];
      const b = trace.path[i];
      if (!a || !b) continue;
      distanceMm = Math.min(distanceMm, distanceToSegment(world, a, b));
    }
    if (distanceMm > toleranceMm) continue;
    if (!best || distanceMm < best.distanceMm) best = { kind: "trace", trace, distanceMm };
  }
  return best;
}

export function containsPoint(box: { x: number; y: number; w: number; h: number }, point: { x: number; y: number }): boolean {
  return point.x >= box.x && point.x <= box.x + box.w && point.y >= box.y && point.y <= box.y + box.h;
}

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function distanceToSegment(
  point: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return distance(point, a);
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared));
  return distance(point, { x: a.x + t * dx, y: a.y + t * dy });
}
